import { Download } from "lucide-react";
import { useI18n } from "../../i18n/I18nProvider";
import { Button } from "../../components/ui/Button";
import { ErrorText, ProgressBar } from "../../components/ui/feedback";
import type { ExportProgress } from "./export-bundle";
import { useConfigExport, type ConfigExport } from "./useConfigExport";

export interface ExportButtonProps {
  workspaceId: string;
  configurationId: string;
  configurationName: string;
  disabled?: boolean;
}

/** Secondary "Download .goalhorns" with its own progress and result line. */
export function ExportButton({
  workspaceId,
  configurationId,
  configurationName,
  disabled = false,
}: ExportButtonProps) {
  const { t } = useI18n();
  const exporter = useConfigExport(workspaceId);
  return (
    <div className="flex min-w-0 flex-col items-end gap-2">
      <Button
        disabled={disabled || exporter.busy}
        onClick={() => void exporter.start(configurationId, configurationName)}
      >
        <Download aria-hidden="true" className="size-[18px]" strokeWidth={1.75} />
        {t("export.button")}
      </Button>
      <ExportStatus exporter={exporter} />
    </div>
  );
}

/** Progress bar while zipping, then the result line or the error. Shared by the list and the lineup. */
export function ExportStatus({ exporter }: { exporter: ConfigExport }) {
  const { t } = useI18n();
  return (
    <div className="flex w-full min-w-0 flex-col items-stretch gap-2 sm:items-end">
      {exporter.progress ? (
        <div className="w-full sm:w-64">
          <ProgressBar
            fraction={fraction(exporter.progress)}
            label={t("export.building", { percent: percent(exporter.progress) })}
          />
        </div>
      ) : null}
      {exporter.message ? (
        <p role="status" className="text-note text-ink-2">
          {exporter.message}
        </p>
      ) : null}
      <ErrorText error={exporter.error} />
    </div>
  );
}

function fraction(progress: ExportProgress): number {
  if (progress.total === 0) return progress.phase === "done" ? 1 : 0;
  return progress.done / progress.total;
}

function percent(progress: ExportProgress): number {
  return Math.round(Math.min(1, Math.max(0, fraction(progress))) * 100);
}
